import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BubbleInput, BubbleButton, BubbleAlert } from '@go-client/components/BubbleUI';
import { safeIdCompare, hasOwnProperty } from '@go-shared/lib/utils';
import { mapBy } from '@go-shared/lib/functional';
import {
  ContentLevelTranslation,
  ContentLevelTransforms,
  ContentLevelTranslatable,
} from '@go-shared/types/translation';
import { replaceTranslationByRefer, getTranslations } from '@go-client/lib/api/translation';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import Fuse from 'fuse.js';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import TranslationBlock from '@go-client/components/TranslationBlock';
import Translations from './Translations';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  generateDefaultNewTranslation,
  makeContentLevelTranslationTransform,
  TranslationFilters,
} from '@go-shared/lib/translation';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandContentLevel, BrandLevel } from '@go-shared/types/brand';
import TranslationsSubNavigation from '../../components/Navigations/TranslationsSubNavigation';
import { canModifyBrandTranslation } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';

interface ContentLevelTranslationDataBundle {
  levels: BrandContentLevel[];
  levelTranslations: ContentLevelTranslation[];
  levelsMap: { [key: string]: BrandContentLevel };
  levelTranslationsMap: { [key: string]: ContentLevelTranslation };
}

interface ContentLevelTranslationForm {
  dirty: boolean;
  transforms: ContentLevelTransforms;
}

interface ChangeContentLevelFormAction {
  active?: boolean;
  valueChange?: { key: keyof ContentLevelTranslatable; value: string };
}

type ContentLevelTranslationsPageProps = RouteComponentProps;

export default function ContentLevelTranslations(props: ContentLevelTranslationsPageProps) {
  const [loading, setLoading] = useState(true);
  const [dataBundle, setDataBundle] = useState<ContentLevelTranslationDataBundle | undefined>(undefined);
  const [activeLevel, setActiveLevel] = useState<BrandLevel | undefined>(undefined);
  const [levelTranslationForm, setLevelTranslationForm] = useState<
    ContentLevelTranslationForm | undefined
  >(undefined);
  const [search, setSearch] = useState('');

  const contentRef = useRef<HTMLDivElement | null>(null);

  const { activeProperty } = useActiveProperty();
  const serverInfo = useServerInfo();
  const { brand, languages } = serverInfo;
  const { user } = useUserInfo();

  useEffect(() => {
    setLoading(true);
    fetchDataBundle().catch(console.error);
  }, [activeProperty]);

  // this will refresh the currently active level if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;

    setActiveLevel(state => {
      const brandLevel = dataBundle.levels[0];
      if (!state) return brandLevel;
      const refreshedLevel = dataBundle.levelsMap[state._id];
      return refreshedLevel || brandLevel;
    });
  }, [dataBundle]);

  // this will reset the form each time a new level is clicked
  useEffect(() => {
    resetLevelsForm();
  }, [activeLevel]);

  // this will scroll the page to top when search is modified
  useEffect(() => {
    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [search, contentRef]);

  const fuse = useMemo(() => {
    if (!dataBundle) return undefined;

    return new Fuse(dataBundle.levels, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [dataBundle]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredLevels = !dataBundle ? [] : fuseSearchResults || dataBundle.levels;

  async function fetchDataBundle() {
    try {
      const levels = brand.contentLevels;
      const levelTranslations = await getTranslations(activeProperty._id, 'contentLevel');

      const levelsMap = mapBy(levels, '_id');
      const levelTranslationsMap = mapBy(levelTranslations, 'refers');

      setDataBundle({
        levels,
        levelTranslations,
        levelsMap,
        levelTranslationsMap,
      });
    } finally {
      setLoading(false);
    }
  }

  function resetLevelsForm() {
    const transforms = activeLevel
      ? makeContentLevelTranslationTransform(
          languages.map(l => l.key),
          dataBundle?.levelTranslationsMap[activeLevel._id]
        )
      : undefined;

    if (!transforms) {
      setLevelTranslationForm(undefined);
      return;
    }

    const formData: ContentLevelTranslationForm = {
      dirty: false,
      transforms,
    };

    setLevelTranslationForm(formData);
  }

  function changeLevelForm(lang: string, change: ChangeContentLevelFormAction) {
    if (!levelTranslationForm) return;

    const updatedForm = { ...levelTranslationForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transforms, lang)) {
      return;
    }

    updatedForm.transforms[lang].modified = true;

    if (change.active !== undefined) updatedForm.transforms[lang].active = change.active;
    if (change.valueChange !== undefined) {
      updatedForm.transforms[lang].delta[change.valueChange.key] = change.valueChange.value;
    }

    setLevelTranslationForm(updatedForm);
  }

  async function handleSubmitLevelForm() {
    if (
      !levelTranslationForm ||
      !activeLevel ||
      !dataBundle ||
      !canModifyBrandTranslation(user, serverInfo)
    )
      return;

    const translation = generateDefaultNewTranslation(activeLevel._id, 'contentLevel');

    translation.transforms = levelTranslationForm.transforms;

    await replaceTranslationByRefer(activeLevel._id, translation);
    await fetchDataBundle();
    setLevelTranslationForm({ ...levelTranslationForm, dirty: false });
  }

  function headerFn() {
    return <TranslationsSubNavigation active="contentLevels" property={activeProperty} />;
  }

  function leftFn() {
    return (
      <>
        <div
          className="sticky flex bg-white border-b"
          style={{ top: 'calc(130px + 40px + 0.75rem)', height: '50px', zIndex: 3 }}
        >
          <BubbleInput
            type="text"
            block
            appearance="bare"
            placeholder="Search..."
            className="flex-grow"
            inputStyle={{ height: '49px' }}
            inputClassName="px-5"
            value={search}
            onValue={setSearch}
          />
        </div>
        <div style={{ paddingTop: '50px' }}>
          {filteredLevels.map(level => (
            <div
              key={level._id}
              className={`list-item px-5 py-3 cursor-pointer ${
                activeLevel && safeIdCompare(activeLevel._id, level._id) ? 'active' : ''
              }`}
              onClick={() => setActiveLevel(level)}
            >
              <div className="flex items-center">
                <div className="overflow-ellipsis">
                  <div className="list-item-name font-semibold">{level.name}</div>
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                />
              </div>
            </div>
          ))}
          {filteredLevels.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No levels found
            </div>
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    return (
      <>
        {dataBundle && activeLevel && levelTranslationForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              {!canModifyBrandTranslation(user, serverInfo) && (
                <BubbleAlert className="mt-3 mx-3" variant="ocean">
                  This level cannot be modified unless you're in a brand instance and a brand admin.
                </BubbleAlert>
              )}
              <TranslationBlock
                label="Name"
                value={activeLevel.name}
                languages={selectedLanguages}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({levelTranslationForm.transforms[language.key].delta.name.length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !levelTranslationForm.transforms[language.key].active ? 'smoke' : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeLevelForm(language.key, {
                          active: !levelTranslationForm.transforms[language.key].active,
                        })
                      }
                      icon={
                        levelTranslationForm.transforms[language.key].active ? 'eye' : 'eye-slash'
                      }
                    />
                    <BubbleInput
                      type="text"
                      block
                      className="flex-grow"
                      inputClassName={
                        !levelTranslationForm.transforms[language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={levelTranslationForm.transforms[language.key].delta.name}
                      onValue={val =>
                        changeLevelForm(language.key, {
                          valueChange: { key: 'name', value: val },
                        })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
            </div>

            <div className="sticky bottom-0 p-3 bg-eggshell border-t">
              <BubbleButton
                onClick={() => handleSubmitLevelForm()}
                variant="navy"
                disabled={
                  !levelTranslationForm.dirty || !canModifyBrandTranslation(user, serverInfo)
                }
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!levelTranslationForm.dirty}
                inverse
                onClick={() => resetLevelsForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if (levelTranslationForm && levelTranslationForm.dirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={levelTranslationForm && levelTranslationForm.dirty}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        category="levels"
        loading={loading}
        contentRef={contentRef}
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
      />
    </>
  );
}
